/**
 * Ticket List Page
 * Filterable ticket queue sorted by risk score.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ticketApi } from '../api/client';
import { Plus, Filter, Search, AlertTriangle, Clock, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const priorityConfig = {
  critical: { label: 'Critical', bg: 'bg-red-500/15', text: 'text-red-400', dot: 'bg-red-500' },
  high: { label: 'High', bg: 'bg-amber-500/15', text: 'text-amber-400', dot: 'bg-amber-500' },
  medium: { label: 'Medium', bg: 'bg-blue-500/15', text: 'text-blue-400', dot: 'bg-blue-500' },
  low: { label: 'Low', bg: 'bg-gray-500/15', text: 'text-gray-400', dot: 'bg-gray-500' },
};

const statusConfig = {
  new: { label: 'New', color: 'text-blue-400' },
  open: { label: 'Open', color: 'text-emerald-400' },
  in_progress: { label: 'In Progress', color: 'text-amber-400' },
  escalated: { label: 'Escalated', color: 'text-red-400' },
  on_hold: { label: 'On Hold', color: 'text-gray-400' },
  pending_client: { label: 'Pending Client', color: 'text-purple-400' },
  resolved: { label: 'Resolved', color: 'text-emerald-500' },
  closed: { label: 'Closed', color: 'text-gray-500' },
};

function RiskBadge({ score }) {
  const getColor = (s) => {
    if (s >= 75) return 'bg-red-500/15 text-red-400';
    if (s >= 50) return 'bg-amber-500/15 text-amber-400';
    if (s >= 25) return 'bg-blue-500/15 text-blue-400';
    return 'bg-emerald-500/15 text-emerald-400';
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getColor(score)}`}>
      {score}
    </span>
  );
}

function SlaIndicator({ responseDue, resolutionDue, responseMet, resolutionMet }) {
  const now = new Date();
  const responseBreached = responseDue && !responseMet && new Date(responseDue) < now;
  const resolutionBreached = resolutionDue && !resolutionMet && new Date(resolutionDue) < now;

  if (responseBreached || resolutionBreached) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-400">
        <Clock className="w-3 h-3" />
        SLA breach
      </span>
    );
  }

  if (responseMet === false || resolutionMet === false) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-400">
        <Clock className="w-3 h-3" />
        At risk
      </span>
    );
  }

  return null;
}

export default function TicketListPage() {
  const { isSentinelUser } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', priority: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const basePath = isSentinelUser ? '/sentinel' : '/portal';

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const params = { page, page_size: 25 };
        if (filters.status) params.status = filters.status;
        if (filters.priority) params.priority = filters.priority;

        const res = await ticketApi.list(params);
        setTickets(res.data.items);
        setTotalPages(res.data.total_pages);
      } catch (err) {
        console.error('Failed to load tickets:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [page, filters]);

  const filteredTickets = searchQuery
    ? tickets.filter((t) =>
        t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.ticket_number.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : tickets;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white">Tickets</h1>
          <p className="text-sm text-gray-500 mt-1">Sorted by risk score, highest first</p>
        </div>
        <Link
          to={`${basePath}/tickets/new`}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Ticket
        </Link>
      </div>

      {/* Filters bar */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600"
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-300 focus:outline-none"
        >
          <option value="">All statuses</option>
          {Object.entries(statusConfig).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={filters.priority}
          onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
          className="px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-300 focus:outline-none"
        >
          <option value="">All priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* Ticket table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Risk</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">SLA</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned</th>
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-gray-800/50">
                  <td colSpan={8} className="px-5 py-4">
                    <div className="h-5 bg-gray-800 rounded animate-pulse" />
                  </td>
                </tr>
              ))
            ) : filteredTickets.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-5 py-12 text-center text-sm text-gray-500">
                  No tickets found
                </td>
              </tr>
            ) : (
              filteredTickets.map((ticket) => {
                const pri = priorityConfig[ticket.priority] || priorityConfig.medium;
                const sts = statusConfig[ticket.status] || statusConfig.open;
                return (
                  <tr
                    key={ticket.id}
                    onClick={() => navigate(`${basePath}/tickets/${ticket.id}`)}
                    className="border-b border-gray-800/50 hover:bg-gray-800/30 cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="text-sm font-medium text-gray-200">{ticket.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{ticket.ticket_number}</div>
                    </td>
                    <td className="px-5 py-4">
                      <RiskBadge score={ticket.risk_score} />
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${pri.bg} ${pri.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${pri.dot}`} />
                        {pri.label}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-sm ${sts.color}`}>{sts.label}</span>
                    </td>
                    <td className="px-5 py-4">
                      <SlaIndicator
                        responseDue={ticket.sla_response_due}
                        resolutionDue={ticket.sla_resolution_due}
                        responseMet={ticket.sla_response_met}
                        resolutionMet={ticket.sla_resolution_met}
                      />
                    </td>
                    <td className="px-5 py-4">
                      {ticket.assignee ? (
                        <span className="text-sm text-gray-300">
                          {ticket.assignee.first_name} {ticket.assignee.last_name?.[0]}.
                        </span>
                      ) : (
                        <span className="text-sm text-gray-600">Unassigned</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                      </span>
                    </td>
                    <td className="px-3 py-4">
                      <ChevronRight className="w-4 h-4 text-gray-600" />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm text-gray-400 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 text-sm text-gray-400 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
